import { useState } from 'react'
import {
  Archive,
  BookOpen,
  Check,
  ExternalLink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { OrganizationAlert } from '@/shared/hooks/api/regulatoryNews'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  formatNewsroomAlertDate,
  formatNewsroomAlertDateTime,
  getNewsroomAlertTitle,
} from '../utils/newsroomAlertDisplay'
import { EuCommissionAvatar } from './EuCommissionAvatar'
import { MatchedCategoryChips } from './MatchedCategoryChips'
import { NewsroomAlertArticleSheet } from './NewsroomAlertArticleSheet'

type NewsroomAlertDetailProps = {
  alert: OrganizationAlert | null
  isMarkReadPending: boolean
  onMarkRead: (alertId: string) => void
  onRequestDismiss: (alertId: string) => void
}

export function NewsroomAlertDetail({
  alert,
  isMarkReadPending,
  onMarkRead,
  onRequestDismiss,
}: NewsroomAlertDetailProps) {
  const { t, i18n } = useTranslation('regulatory-alerts')
  const [articleSheetOpen, setArticleSheetOpen] = useState(false)

  if (!alert) {
    return (
      <div className='flex flex-1 items-center justify-center p-8'>
        <p className='text-sm text-muted-foreground'>{t('inbox.selectAlert')}</p>
      </div>
    )
  }

  const title = getNewsroomAlertTitle(alert, t('inbox.noTitle'))
  const publishedDateTime = formatNewsroomAlertDateTime(
    alert.publishedAt,
    i18n.language
  )
  const derivedDate = formatNewsroomAlertDate(alert.derivedAt, i18n.language)
  const headerDate =
    publishedDateTime ??
    formatNewsroomAlertDateTime(alert.derivedAt, i18n.language)

  const showMarkRead = alert.readAt === null
  const showDismiss = alert.dismissedAt === null
  const hasArticleLink = Boolean(alert.link)

  return (
    <div className='flex h-full min-h-0 flex-col'>
      <TooltipProvider>
        <div className='flex items-center gap-1 p-2'>
          {showMarkRead ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-9'
                  disabled={isMarkReadPending}
                  aria-label={t('inbox.markRead')}
                  onClick={() => {
                    onMarkRead(alert.id)
                  }}
                >
                  <Check className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('inbox.markRead')}</TooltipContent>
            </Tooltip>
          ) : null}
          {showDismiss ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='icon'
                  className='size-9'
                  aria-label={t('inbox.dismiss')}
                  onClick={() => {
                    onRequestDismiss(alert.id)
                  }}
                >
                  <Archive className='size-4' />
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t('inbox.dismiss')}</TooltipContent>
            </Tooltip>
          ) : null}
          {hasArticleLink ? (
            <div className='ms-auto flex items-center gap-1'>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type='button'
                    variant='ghost'
                    size='icon'
                    className='size-9'
                    aria-label={t('inbox.readFullArticle')}
                    onClick={() => {
                      setArticleSheetOpen(true)
                    }}
                  >
                    <BookOpen className='size-4' />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('inbox.readFullArticle')}</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    asChild
                    variant='ghost'
                    size='icon'
                    className='size-9'
                  >
                    <a
                      href={alert.link ?? undefined}
                      target='_blank'
                      rel='noopener noreferrer'
                      aria-label={t('inbox.openArticle')}
                    >
                      <ExternalLink className='size-4' />
                    </a>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('inbox.openArticle')}</TooltipContent>
              </Tooltip>
            </div>
          ) : null}
        </div>
      </TooltipProvider>

      <Separator />

      <div className='flex min-h-0 flex-1 flex-col'>
        <div className='flex items-start gap-4 p-4'>
          <EuCommissionAvatar />
          <div className='grid min-w-0 flex-1 gap-1'>
            <div className='font-semibold'>{t('inbox.sourceEuCommission')}</div>
            <div className='line-clamp-2 text-sm'>{title}</div>
            {derivedDate ? (
              <div className='line-clamp-1 text-xs text-muted-foreground'>
                {t('inbox.derived', { date: derivedDate })}
              </div>
            ) : null}
          </div>
          {headerDate ? (
            <div className='shrink-0 text-xs text-muted-foreground'>
              {headerDate}
            </div>
          ) : null}
        </div>

        <Separator />

        <ScrollArea className='min-h-0 flex-1'>
          <div className='space-y-4 p-4 text-sm'>
            {alert.descriptionText ? (
              <p className='whitespace-pre-wrap leading-relaxed'>
                {alert.descriptionText}
              </p>
            ) : null}
            <MatchedCategoryChips
              categories={alert.matchedCategories}
              label={t('inbox.whyYouSawThis')}
            />
          </div>
        </ScrollArea>
      </div>

      <NewsroomAlertArticleSheet
        alert={alert}
        open={articleSheetOpen}
        onOpenChange={setArticleSheetOpen}
      />
    </div>
  )
}
